package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.model.ActiveWhiteboard
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.repository.GameEventRepository
import fr.manyverse.app.domain.repository.WhiteboardRepository
import fr.manyverse.app.domain.validation.WhiteboardValidator

/**
 * Use case for opening a whiteboard.
 * Reuses an existing whiteboard for the table if one exists,
 * otherwise creates a new one.
 */
class OpenWhiteboardUseCase(
    private val whiteboardRepository: WhiteboardRepository,
    private val gameEventRepository: GameEventRepository
) {
    /**
     * Opens a whiteboard (get-or-create pattern).
     *
     * @param tableId The game table ID
     * @param createdBy The user ID of the creator (GM)
     * @param targetParticipants List of user IDs who can see the whiteboard
     * @return Result containing the ActiveWhiteboard
     */
    suspend operator fun invoke(
        tableId: String,
        createdBy: String,
        targetParticipants: List<String>,
        showGrid: Boolean = false
    ): Result<ActiveWhiteboard> {
        // Validate participants
        WhiteboardValidator.validateForCreation(targetParticipants).onFailure {
            return Result.failure(it)
        }

        // Try to find an existing whiteboard for this table
        val existingResult = whiteboardRepository.getWhiteboardForTable(
            tableId = tableId
        )

        existingResult.onFailure {
            return Result.failure(it)
        }

        val existing = existingResult.getOrNull()

        val whiteboardResult = if (existing != null) {
            // Reactivate existing whiteboard with new participants
            whiteboardRepository.reactivateWhiteboard(
                whiteboardId = existing.id,
                targetParticipants = targetParticipants,
                showGrid = showGrid
            )
        } else {
            // Create a new whiteboard
            whiteboardRepository.createWhiteboard(
                tableId = tableId,
                createdBy = createdBy,
                targetParticipants = targetParticipants,
                showGrid = showGrid
            )
        }

        whiteboardResult.onFailure {
            return Result.failure(it)
        }

        val whiteboard = whiteboardResult.getOrThrow()

        // Record the event in history
        val content = EventContent.WhiteboardOpened(
            whiteboardId = whiteboard.id
        )

        gameEventRepository.createEvent(
            tableId = tableId,
            type = EventType.WHITEBOARD_OPENED,
            triggeredBy = createdBy,
            targetParticipants = targetParticipants,
            content = content
        )

        return Result.success(whiteboard)
    }
}
